import { DateTime } from "luxon";
import { ColDef } from "../columns/colDef";

export type ValueGetter = (params: {
  data?: unknown;
  colDef?: ColDef;
}) => unknown;

function getValue(
  params: { data?: any; colDef?: ColDef },
  config?: ValueGetterConfig
): unknown {
  const { data, colDef } = params;
  if (
    data === undefined ||
    colDef === undefined ||
    colDef.field === undefined
  ) {
    return config?.defaultValue ?? undefined;
  }
  const field = colDef.field;
  return data[field];
}

export interface BooleanValueGetterConfig extends ValueGetterConfig {
  trueDisplay: string;
  falseDisplay: string;
}

export const booleanValueGetterFactory: (
  config?: BooleanValueGetterConfig
) => ValueGetter = (config) => (params) => {
  config ??= { trueDisplay: "Yes", falseDisplay: "No" };

  const value = getValue(params, config);
  if (value === undefined || value === null) return undefined;

  return value ? config.trueDisplay : config.falseDisplay;
};

export const moneyValueGetterFactory: (
  config?: NumberValueGetterConfig
) => ValueGetter = (config) => (params) => {
  config = { decimalPlaces: 2, ...config };
  const value = decimalValueGetterFactory(config)(params);
  return value ? `$${value}` : undefined;
};

export const percentageValueGetterFactory: (
  config?: NumberValueGetterConfig
) => ValueGetter = (config) => (params) => {
  const value = decimalValueGetterFactory(config)(params);
  return value ? `${value}%` : undefined;
};

export const decimalValueGetterFactory: (
  config?: NumberValueGetterConfig
) => ValueGetter = (config) => (params) => {
  const value = <number>getValue(params, config);
  config ??= { decimalPlaces: 3 };
  return value === undefined || value === null
    ? undefined
    : `${value.toFixed(config.decimalPlaces)}`;
};

export const lookupValueGetterFactory: (
  config: OptionsValueGetterConfig
) => ValueGetter = (config) => (params) => {
  const value = <string>getValue(params, config);
  if (value === undefined) {
    return undefined;
  }

  if (!Object.keys(config.lookup).includes(value)) {
    return value;
  }
  return config.lookup[value];
};

export interface ValueGetterConfig {
  defaultValue?: unknown;
}

export interface NumberValueGetterConfig extends ValueGetterConfig {
  decimalPlaces?: number;
}

export interface OptionsValueGetterConfig extends ValueGetterConfig {
  lookup: { [value: string]: string };
}

export const dateValueGetter: ValueGetter = getDateFormatter(
  DateTime.DATE_SHORT
);
export const dateTimeValueGetter: ValueGetter = getDateFormatter(
  DateTime.DATETIME_SHORT
);

function getDateFormatter(options: Intl.DateTimeFormatOptions): ValueGetter {
  return function (args) {
    const value = getValue(args);
    if (!value) {
      return undefined;
    }
    const luxonObject =
      value instanceof Date
        ? DateTime.fromJSDate(value)
        : DateTime.fromISO(<string>value);
    return luxonObject.toLocaleString(options);
  };
}
